import { useEffect, useState, useRef, useCallback } from 'react'
import { useApp } from '../context/AppContext'
import { sileo } from 'sileo'
import ticketHistoryService from '../services/ticketHistoryService'

// ─────────────────────────────────────────────
// CONSTANTES Y HELPERS
// ─────────────────────────────────────────────

/** @type {{TICKETS: string, PUBLIC: string}} Esquemas de base de datos */
const SCHEMA = { TICKETS: 'Tickets', PUBLIC: 'public' }

/** @type {{TICKET: string, STATUS: string, TIME_LOG: string, EMPLEADOS: string}} Nombres de tablas */
const TABLE = {
  TICKET: 'ticket',
  STATUS: 'status',
  TIME_LOG: 'ticketTimeLog',
  EMPLEADOS: 'Empleados',
}

/**
 * Parsea una cadena de fecha a objeto Date asegurando el formato UTC.
 * @param {string|null} dateString - Fecha en formato ISO o similar.
 * @returns {Date|null}
 */
function parseUTC(dateString) {
  if (!dateString) return null
  return new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z')
}

/**
 * Formatea segundos totales en una cadena HH:MM:SS.
 * @param {number} totalSeconds - Segundos a formatear.
 * @returns {string}
 */
export function formatTime(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  const pad = (n) => n.toString().padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

// ─────────────────────────────────────────────
// HOOK PRINCIPAL
// ─────────────────────────────────────────────

/**
 * Hook para la gestión integral de un ticket individual.
 * Maneja estados de carga, información del responsable, contador de tiempo,
 * etiquetas y acciones de actualización.
 * * @param {string|number} id - Identificador único del ticket.
 * @param {Object} currentUser - Objeto del usuario actual autenticado.
 * @returns {Object} Estado del ticket y funciones de acción.
 */
export const useTicket = (id, currentUser) => {
  const { supabase, user } = useApp()

  /** Nombre legible del actor para el historial de cambios. */
  const actorName = user?.user_metadata?.name || user?.email || 'Usuario'

  // Estados con los nombres que pide tu componente
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(true)
  const [employee, setEmployee] = useState(null) // Antes era responsible
  const [status, setStatus] = useState([])
  const [time, setTime] = useState(0)
  const [running, setRunning] = useState(false)
  const [activeLog, setActiveLog] = useState(null)
  const [tags, setTags] = useState([])

  const baseTimeRef = useRef(0)

  /**
   * Carga los datos del empleado (responsable).
   * @param {number} [overrideId] - ID opcional para evitar cierres de estado viejos (closures)
   */
  const fetchEmployee = useCallback(
    async (overrideId) => {
      const targetId = overrideId || ticket?.responsibleId

      if (!targetId) {
        setEmployee(null)
        return
      }

      try {
        const { data, error } = await supabase
          .schema(SCHEMA.PUBLIC)
          .from(TABLE.EMPLEADOS)
          .select('*')
          .eq('id', targetId)
          .single()

        if (error) throw error
        setEmployee(data)
      } catch (error) {
        console.error('Error cargando empleado:', error)
        setEmployee(null)
      }
    },
    [supabase, ticket?.responsibleId]
  )

  /**
   * Recupera los logs de tiempo del ticket y calcula el tiempo acumulado.
   * @async
   */
  const loadTimeData = useCallback(async () => {
    try {
      const { data: logs, error } = await supabase
        .schema(SCHEMA.TICKETS)
        .from(TABLE.TIME_LOG)
        .select('*')
        .eq('ticketId', id)

      if (error) throw error

      let accumulatedSeconds = 0
      let openLog = null

      logs.forEach((log) => {
        if (log.endAt) accumulatedSeconds += log.durationSeconds ?? 0
        else openLog = log
      })

      baseTimeRef.current = accumulatedSeconds
      setTime(accumulatedSeconds)
      setActiveLog(openLog)
      setRunning(Boolean(openLog))
    } catch (error) {
      sileo.error({ title: 'Error tiempo', description: error.message })
    }
  }, [id, supabase])

  /**
   * Obtiene las etiquetas (tags) asociadas al ticket actual.
   * @async
   */
  const fetchTags = useCallback(async () => {
    try {
      const { data: tagsData, error } = await supabase
        .schema('Tickets')
        .from('ticketTag')
        .select('tag:tagId(id, name)') // Relaciona tagId con la tabla tag y renombra a 'tag'
        .eq('ticketId', id)

      if (error) throw error

      // Extraemos solo el objeto 'tag' de cada fila para evitar errores de referencia
      const formattedTags =
        tagsData?.map((item) => item.tag).filter(Boolean) || []
      setTags(formattedTags)
    } catch (error) {
      console.error('Error fetching tags:', error)
    }
  }, [id])

  /**
   * Obtiene la información principal del ticket y dispara la carga de tiempo.
   * @async
   */
  const getTicket = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .schema(SCHEMA.TICKETS)
        .from(TABLE.TICKET)
        .select('*')
        .eq('id', id)
        .eq('deleted', false)
        .single()

      if (error) throw error
      setTicket(data)
      await loadTimeData()
    } catch (error) {
      sileo.error({ title: 'Error interno', description: error.message })
    } finally {
      setLoading(false)
    }
  }, [id, supabase, loadTimeData])

  /**
   * Obtiene el catálogo completo de estados disponibles para tickets.
   * @async
   */
  const getStatusList = useCallback(async () => {
    const { data } = await supabase
      .schema(SCHEMA.TICKETS)
      .from(TABLE.STATUS)
      .select('*')
    if (data) setStatus(data)
  }, [supabase])

  // ACCIONES

  /**
   * Aplica cambios ya persistidos por otra vía (p. ej. la transferencia de
   * cola) sin recargar todo el ticket. Si cambia el responsable, el efecto de
   * `fetchEmployee` actualiza su tarjeta.
   * @param {Object} changes
   */
  const patchTicket = useCallback(
    (changes) => setTicket((prev) => (prev ? { ...prev, ...changes } : prev)),
    []
  )

  /**
   * Asigna un empleado como responsable del ticket.
   * @async
   * @param {number|string} userId - ID del empleado a asignar.
   */
  const assignResponsible = useCallback(
    async (userId) => {
      try {
        // Registrar la primera respuesta (SLA) si aún no existe: asignar un
        // responsable cuenta como que alguien tomó el caso.
        const firstResponsePatch =
          ticket && !ticket.firstResponseAt
            ? { firstResponseAt: new Date().toISOString() }
            : {}

        const { error } = await supabase
          .schema(SCHEMA.TICKETS)
          .from(TABLE.TICKET)
          .update({ responsibleId: userId, ...firstResponsePatch })
          .eq('id', id)

        if (error) throw error

        const prevResponsible = employee
          ? `${employee.nombre} ${employee.apellido || ''}`.trim()
          : 'Sin asignar'

        setTicket((prev) => ({
          ...prev,
          responsibleId: userId,
          ...firstResponsePatch,
        }))

        // FIX: Llamamos a fetchEmployee pasando el ID nuevo directamente
        // para que no dependa del re-render de 'ticket'
        await fetchEmployee(userId)

        // Historial: nombre del nuevo responsable
        let newResponsible = `#${userId}`
        try {
          const { data: emp } = await supabase
            .schema(SCHEMA.PUBLIC)
            .from(TABLE.EMPLEADOS)
            .select('nombre, apellido')
            .eq('id', userId)
            .single()
          if (emp) newResponsible = `${emp.nombre} ${emp.apellido || ''}`.trim()
        } catch {
          /* nombre opcional */
        }

        ticketHistoryService.log({
          ticketId: id,
          fieldName: 'Responsable',
          oldValue: prevResponsible,
          newValue: newResponsible,
          changedBy: actorName,
        })

        sileo.success({ title: 'Responsable asignado' })
        getStatusList()
      } catch (error) {
        sileo.error({ title: 'Error al asignar', description: error.message })
      }
    },
    [id, supabase, fetchEmployee, getStatusList, ticket, employee, actorName]
  )

  /**
   * Cambia el estado actual del ticket.
   * @async
   * @param {number|string} statusId - ID del nuevo estado.
   */
  const changeStatus = useCallback(
    async (statusId) => {
      // Cambiar el estado también cuenta como primera respuesta (SLA).
      const firstResponsePatch =
        ticket && !ticket.firstResponseAt
          ? { firstResponseAt: new Date().toISOString() }
          : {}

      const { error } = await supabase
        .schema(SCHEMA.TICKETS)
        .from(TABLE.TICKET)
        .update({ statusId, ...firstResponsePatch })
        .eq('id', id)
      if (!error) {
        const nameOf = (sid) =>
          status.find((s) => Number(s.id) === Number(sid))?.name || `#${sid}`
        ticketHistoryService.log({
          ticketId: id,
          fieldName: 'Estado',
          oldValue: nameOf(ticket?.statusId),
          newValue: nameOf(statusId),
          changedBy: actorName,
        })
        setTicket((prev) => ({ ...prev, statusId, ...firstResponsePatch }))
        sileo.success({ title: 'Estado Actualizado' })
      }
    },
    [id, supabase, ticket, status, actorName]
  )

  /**
   * Alterna el estado de finalización del ticket y registra la fecha de resolución.
   * @async
   */
  const toggleCompleted = useCallback(async () => {
    const nextVal = !ticket?.completed
    // Al completar: marcar resolvedAt. Al reabrir: limpiarlo (no falsear el SLA).
    const resolvedAt = nextVal ? new Date().toISOString() : null
    const { error } = await supabase
      .schema(SCHEMA.TICKETS)
      .from(TABLE.TICKET)
      .update({ completed: nextVal, resolvedAt })
      .eq('id', id)

    if (!error) {
      ticketHistoryService.log({
        ticketId: id,
        fieldName: 'Finalización',
        oldValue: ticket?.completed ? 'Completado' : 'Abierto',
        newValue: nextVal ? 'Completado' : 'Reabierto',
        changedBy: actorName,
      })
      setTicket((prev) => ({ ...prev, completed: nextVal, resolvedAt }))
      nextVal
        ? sileo.success({ title: 'Ticket Completado' })
        : sileo.warning({ title: 'Ticket Reabierto' })
    }
  }, [id, ticket?.completed, supabase, actorName])

  /**
   * Inicia el registro de tiempo (timer) para el usuario actual.
   * @async
   */
  const start = useCallback(async () => {
    const { data, error } = await supabase
      .schema(SCHEMA.TICKETS)
      .from(TABLE.TIME_LOG)
      .insert({
        ticketId: id,
        userId: currentUser?.id,
        startAt: new Date().toISOString(),
      })
      .select()
      .single()

    if (!error) {
      setActiveLog(data)
      setRunning(true)
    }
  }, [id, currentUser?.id, supabase])

  /**
   * Detiene el registro de tiempo activo y calcula la duración final.
   * @async
   */
  const stop = useCallback(async () => {
    if (!activeLog) return
    const now = new Date()
    const startDate = parseUTC(activeLog.startAt)
    const durationSeconds = Math.floor((now - startDate) / 1000)

    const { error } = await supabase
      .schema(SCHEMA.TICKETS)
      .from(TABLE.TIME_LOG)
      .update({ endAt: now.toISOString(), durationSeconds })
      .eq('id', activeLog.id)

    if (!error) {
      baseTimeRef.current += durationSeconds
      setTime(baseTimeRef.current)
      setActiveLog(null)
      setRunning(false)
    }
  }, [activeLog, supabase])

  /**
   * Dispara manualmente la actualización de las etiquetas del ticket.
   */
  const RefreshTags = () => {
    fetchTags()
  }

  // EFECTOS
  useEffect(() => {
    if (id) {
      getTicket()
      ;(getStatusList(), fetchTags())
    }
  }, [id, getTicket, getStatusList])

  // Sincronización del empleado cuando cambia el ID de responsable en el ticket
  useEffect(() => {
    fetchEmployee()
  }, [ticket?.responsibleId, fetchEmployee])

  // Timer interval
  useEffect(() => {
    if (!activeLog || activeLog.endAt) return
    const interval = setInterval(() => {
      const startDate = parseUTC(activeLog.startAt)
      const liveSeconds = Math.floor((new Date() - startDate) / 1000)
      setTime(baseTimeRef.current + liveSeconds)
    }, 1000)
    return () => clearInterval(interval)
  }, [activeLog])

  return {
    ticket,
    loading,
    employee, // Ahora se llama employee como en el componente
    status,
    time,
    running,
    tags,
    formatTime,
    changeStatus,
    RefreshTags,
    assignResponsible,
    toggleCompleted,
    start,
    stop,
    patchTicket,
  }
}
